import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Send, Loader2, Coins, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { createSSELineBuffer, processSSEChunk, flushAndProcessSSE } from '@/lib/sseParser';
import { getUserEntitlements, type UserEntitlements } from '@/lib/entitlements';
import { useUserRole } from '@/hooks/useUserRole';
import AIContextDebugBadge from './AIContextDebugBadge';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function AIGuidanceChat() {
  const { goBack, getBackButtonLabel } = useNavigationHistory();
  const [displayName, setDisplayName] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [entitlements, setEntitlements] = useState<UserEntitlements | null>(null);
  const [authUser, setAuthUser] = useState<import('@supabase/supabase-js').User | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isAdmin } = useUserRole(authUser);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { totalCredits, checkCredits } = useSubscription();

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', user.id)
        .single();
      
      if (data?.display_name) {
        const firstName = data.display_name.split(' ')[0];
        setDisplayName(firstName);
        
        // Set initial greeting with name
        const greeting = `Hello ${firstName}! I'm your Soul Guidance companion with access to your complete journey-patterns, biometrics, sleep data, activities, predictions, and more. I can help you interpret intuitive messages, spirit guidance, and psychic impressions in the context of your unique path. Share your experiences, symbols, or messages, and I'll help you understand their deeper meaning. What would you like to explore?`;
        setMessages([{ role: 'assistant', content: greeting }]);
      } else {
        // Set default greeting without name
        setMessages([{
          role: 'assistant',
          content: "Hello! I'm your Soul Guidance companion with access to your complete journey-patterns, biometrics, sleep data, activities, predictions, and more. I can help you interpret intuitive messages, spirit guidance, and psychic impressions in the context of your unique path. Share your experiences, symbols, or messages, and I'll help you understand their deeper meaning. What would you like to explore?"
        }]);
      }
      // Fetch entitlements + set auth user for role check
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        setAuthUser(currentUser);
        getUserEntitlements(currentUser.id).then(setEntitlements);
      }
    };
    
    fetchProfile();
    checkCredits();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // Check credits before sending
    if (totalCredits <= 0) {
      toast({
        title: 'No credits remaining',
        description: 'Purchase credits to continue conversations',
        variant: 'destructive',
        action: (
          <Button size="sm" onClick={() => navigate('/credits')}>
            Get Credits
          </Button>
        ),
      });
      return;
    }

    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Server deducts credit via edge function; client only refreshes balance

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `https://jbdsdzmyqkfbqijnoqmo.supabase.co/functions/v1/guidance-interpreter`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            messages: [...messages, userMessage].map(m => ({
              role: m.role,
              content: m.content
            }))
          })
        }
      );

      if (response.status === 429) {
        toast({
          title: 'Too many requests',
          description: 'Please wait a moment before trying again.',
          variant: 'destructive'
        });
        setMessages(prev => prev.slice(0, -1));
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      const sseBuf = createSSELineBuffer();

      if (reader) {
        setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) {
            // Flush any trailing data without final newline
            const final = flushAndProcessSSE(sseBuf);
            assistantMessage += final.texts.join('');
            break;
          }

          const { texts, done: isDone } = processSSEChunk(sseBuf, decoder.decode(value, { stream: true }));
          if (texts.length > 0) {
            assistantMessage += texts.join('');
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1].content = assistantMessage;
              return newMessages;
            });
          }
          if (isDone) { streamDone = true; break; }
        }
      }

      // Server already deducted credit; refresh client balance
      if (assistantMessage.trim().length > 0) {
        // Update final message content
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1].content = assistantMessage;
          return newMessages;
        });
        await checkCredits();
      } else {
        // Remove empty assistant message
        setMessages(prev => prev.filter(m => m.content !== ''));
      }
    } catch (error) {
      
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4">
      <Button 
        onClick={goBack}
        variant="ghost" 
        className="mb-4"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {getBackButtonLabel()}
      </Button>
      
      <Card className="h-[600px] flex flex-col">
        <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Soul Guidance
          </CardTitle>
          <Badge variant="outline" className="gap-1">
            <Coins className="h-3 w-3" />
            {totalCredits} credits
          </Badge>
        </div>
        <CardDescription>
          AI-powered Soul Guidance for intuitive messages, spirit communication, and psychic impressions
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4 overflow-hidden">
        <AIContextDebugBadge entitlements={entitlements} isAdmin={isAdmin} />
        <ScrollArea className="flex-1 pr-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((message, idx) => (
              <div
                key={idx}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground border border-border'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg p-3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Describe your intuitive experience, symbols, or guidance received..."
            className="min-h-[60px]"
            disabled={isLoading}
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="h-[60px] w-[60px]"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}
